import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { Icon, IconName, Text, HStack, NotificationCounter, Box } from '@televet/kibble-ui';
import { useSidePanel } from 'shared/components/SidePanel/state/providers/SidePanelProvider';
import { PanelViews } from 'shared/components/SidePanel/shared/types/enums/PanelViews';
import Tooltip from 'shared/components/Tooltip';
import { CSSProperties } from 'styled-components';
import { useTracking } from 'shared/providers/TrackingProvider';
import { EventName } from 'shared/providers/TrackingProvider/EventNames';

interface NavButtonProps {
  leftIconName: IconName;
  rightIconName?: IconName;
  title: PanelViews;
  to: string;
  badgeCount?: number;
  isOpen?: boolean;
  id: string;
  eventName?: EventName;
}

const NavButton = ({
  leftIconName,
  rightIconName,
  title,
  to,
  badgeCount,
  isOpen,
  id,
  eventName,
}: NavButtonProps): JSX.Element | null => {
  const { isMobile } = useResolutionProvider();
  const { setIsOpen, setCurrentView } = useSidePanel();
  const { trackEvent } = useTracking();

  const closeSidePanel = (): void => {
    setCurrentView(title);
    setIsOpen(false);
  };

  const absolutePositionStyles = useMemo((): CSSProperties => {
    return {
      top: `${isMobile ? 10 : isOpen ? 12 : 6}px`,
      right: `calc(50% - ${isMobile ? '20px' : isOpen ? '80px' : '24px'})`,
      transition: 'all 0.4s ease-out',
      position: 'absolute',
    };
  }, [isMobile, isOpen]);
  return (
    <Tooltip
      className="NavButton__ViewItem-Tooltip"
      disabled={isOpen}
      content={title}
      placement="right"
      delay={[750, 0]}
    >
      <Box
        as={NavLink}
        to={to}
        onClick={(): void => {
          eventName && trackEvent(eventName);
          isMobile && closeSidePanel();
        }}
        id={id}
        w="100%"
        h={isMobile ? '64px' : isOpen ? '44px' : '56px'}
        p={isMobile ? 0 : isOpen ? '12px 20px' : 4}
        pos="relative"
        display="flex"
        alignItems="center"
        justifyContent={isMobile ? 'center' : 'left'}
        className="NavButton__ViewItem"
        textDecor="none"
        color="text.subtle"
        _hover={{
          bg: 'background.primaryAlpha.hover',
        }}
        _activeLink={{
          bg: 'background.primaryAlpha.hover',
          color: 'text.brand',
        }}
      >
        {badgeCount !== undefined && (
          <Box className="NavButton__ViewItem-NotificationCounter" style={absolutePositionStyles}>
            {badgeCount > 0 && <NotificationCounter count={badgeCount} />}
          </Box>
        )}
        {rightIconName && (
          <Box className="NavButton__ViewItem-RightIcon" style={absolutePositionStyles}>
            <Icon size={isOpen ? 'lg' : 'sm'} name={rightIconName} variant="danger" />
          </Box>
        )}
        <HStack
          className="NavButton__ViewItem-Container"
          width="100%"
          flexDirection={isMobile ? 'column' : 'row'}
          align="center"
          color="inherit"
        >
          <Box className="NavButton__ViewItem-Icon" h={isMobile ? '32px' : isOpen ? 5 : 6}>
            <Icon size={isMobile ? 'xl' : isOpen ? 'md' : 'lg'} name={leftIconName} />
          </Box>
          <Text
            className="NavButton__ViewItem-Name"
            overflow="hidden"
            whiteSpace="nowrap"
            size="sm"
            color="inherit"
            display={isMobile ? 'none' : 'block'}
          >
            {title}
          </Text>
        </HStack>
      </Box>
    </Tooltip>
  );
};

export default NavButton;
